import Box from "@cloudscape-design/components/box";
import Button from "@cloudscape-design/components/button";
import FormField from "@cloudscape-design/components/form-field";
import Icon from "@cloudscape-design/components/icon";
import SpaceBetween from "@cloudscape-design/components/space-between";
import { useState, useRef } from "react";
import { useReviewImageUpload } from "../../hooks/useReviewImageUpload";
import { DraggableImageList } from "./DraggableImageList";

interface ReviewImageUploadProps {
  tempId: string;
  idToken: string;
  onImagesChange: (images: Array<{ key: string; viewUrl: string; hash: string }>) => void;
  maxImages?: number;
  existingImages?: string[];
  onExistingImagesChange?: (images: string[]) => void;
}

export const ReviewImageUpload = ({
  tempId,
  idToken,
  onImagesChange,
  maxImages = 5,
  existingImages = [],
  onExistingImagesChange
}: ReviewImageUploadProps) => {
  const [images, setImages] = useState<Array<{ key: string; viewUrl: string; hash: string; file?: File }>>([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadImage, uploading, uploadError, clearError } = useReviewImageUpload({ tempId, idToken });

  const handleFileSelect = async (files: FileList | null) => {
    const totalImages = images.length + existingImages.length;
    if (!files || totalImages >= maxImages) return;

    const remainingSlots = maxImages - totalImages;
    const filesToProcess = Array.from(files).slice(0, remainingSlots);
    const newImages = [...images];

    for (const file of filesToProcess) {
      clearError();
      const result = await uploadImage(file);
      if (result) {
        newImages.push({ ...result, file });
      } else if (uploadError) {
        alert(uploadError);
      }
    }

    setImages(newImages);
    onImagesChange(newImages.map(img => ({ key: img.key, viewUrl: img.viewUrl, hash: img.hash })));
  };

  const removeImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    onImagesChange(newImages.map(img => ({ key: img.key, viewUrl: img.viewUrl, hash: img.hash })));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  return (
    <FormField
      label="Review Images (Optional)"
      description={`Add up to ${maxImages} images to your review. JPG and PNG only, max 5MB each.`}
    >
      <SpaceBetween size="s">
        {/* Upload Area */}
        {(images.length + existingImages.length) < maxImages && (
          <div
            key="upload-area"
            style={{
              padding: '24px',
              textAlign: 'center',
              border: `2px dashed ${dragOver ? '#0972d3' : '#d5dbdb'}`,
              borderRadius: '8px',
              backgroundColor: dragOver ? '#f2f8fd' : '#fafbfc',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => {
              console.log('Upload area clicked, fileInputRef:', fileInputRef.current);
              fileInputRef.current?.click();
            }}
          >
            <SpaceBetween size="s">
              <Icon key="upload-icon" name="upload" size="large" />
              <Box key="upload-text">
                <Box fontWeight="bold">Drop images here or click to browse</Box>
                <Box fontSize="body-s" color="text-body-secondary">
                  {maxImages - images.length - existingImages.length} of {maxImages} slots remaining
                </Box>
              </Box>
            </SpaceBetween>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/jpg,image/png"
              style={{ display: 'none' }}
              onChange={(e) => {
                console.log('File input changed:', e.target.files);
                handleFileSelect(e.target.files);
              }}
            />
          </div>
        )}

        {/* Image Previews */}
        {(existingImages.length > 0 || images.length > 0) && (
          <Box key="image-previews">
            <Box marginBottom="s" fontWeight="bold">Review Images:</Box>
            <DraggableImageList
              images={[...existingImages, ...images.map(img => img.viewUrl)]}
              onReorder={(reordered) => {
                const existingCount = existingImages.length;
                const newExisting = reordered.slice(0, existingCount);
                const newUploaded = reordered.slice(existingCount);
                
                if (onExistingImagesChange) {
                  onExistingImagesChange(newExisting);
                }
                
                const reorderedImages = newUploaded.map(url => 
                  images.find(img => img.viewUrl === url)!
                ).filter(Boolean);
                setImages(reorderedImages);
                onImagesChange(reorderedImages.map(img => ({ key: img.key, viewUrl: img.viewUrl, hash: img.hash })));
              }}
              onRemove={(index) => {
                if (index < existingImages.length) {
                  if (onExistingImagesChange) {
                    onExistingImagesChange(existingImages.filter((_, i) => i !== index));
                  }
                } else {
                  removeImage(index - existingImages.length);
                }
              }}
            />
          </Box>
        )}

        {uploading && (
          <Box key="uploading" textAlign="center" color="text-body-secondary">
            <Icon name="status-in-progress" /> Uploading images...
          </Box>
        )}
        
        {uploadError && (
          <Box key="upload-error" color="text-status-error" fontSize="body-s">
            {uploadError}
          </Box>
        )}
      </SpaceBetween>
    </FormField>
  );
};